import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { authService, cashService, charactersService } from '../services/api';
import { useLanguage } from '../context/LanguageContext';
import PromoCodeSection from '../components/PromoCodeSection';
import '../styles/UserCP.css';

// Mobile menu script
const initMobileMenu = () => {
    const toggle = document.querySelector('.mobile-menu-toggle');
    const nav = document.querySelector('.sidebar-nav');

    if (toggle && nav) {
        toggle.addEventListener('click', function () {
            nav.classList.toggle('active');
            const icon = toggle.querySelector('i');
            if (icon) {
                if (nav.classList.contains('active')) {
                    icon.className = 'ri-close-line';
                } else {
                    icon.className = 'ri-menu-line';
                }
            }
        });
    }
};

const UserCP: React.FC = () => {
    const navigate = useNavigate();
    const { t, locale } = useLanguage();
    const dateLocale = locale === 'pt-BR' ? 'pt-BR' : 'en-US';
    const [user, setUser] = useState<any>(null);
    const [cashBalance, setCashBalance] = useState({ cashBalance: 0, bonusBalance: 0, totalBalance: 0 });
    const [characters, setCharacters] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    const [pwdCurrent, setPwdCurrent] = useState('');
    const [pwdNew, setPwdNew] = useState('');
    const [pwdConfirm, setPwdConfirm] = useState('');
    const [pwdMsg, setPwdMsg] = useState<{ ok: boolean; text: string } | null>(null);
    const [pwdSaving, setPwdSaving] = useState(false);

    const handleChangePassword = async (e: React.FormEvent) => {
        e.preventDefault();
        setPwdMsg(null);

        if (pwdNew.length < 6) {
            setPwdMsg({ ok: false, text: 'A nova senha deve ter pelo menos 6 caracteres.' });
            return;
        }
        if (pwdNew !== pwdConfirm) {
            setPwdMsg({ ok: false, text: 'As novas senhas não coincidem.' });
            return;
        }

        setPwdSaving(true);
        try {
            const res = await authService.changePassword({
                currentPassword: pwdCurrent,
                newPassword: pwdNew,
                confirmNewPassword: pwdConfirm,
            });
            setPwdMsg({ ok: !!res.success, text: res.message });
            if (res.success) {
                setPwdCurrent('');
                setPwdNew('');
                setPwdConfirm('');
            }
        } catch (err: any) {
            const data = err?.response?.data;
            setPwdMsg({ ok: false, text: data?.message || 'Erro ao alterar senha. Tente novamente.' });
        } finally {
            setPwdSaving(false);
        }
    };

    // Debug: log quando componente renderiza
    console.log('UserCP renderizando. User:', user?.account, 'Characters:', characters.length, 'Loading:', loading);

    // Verificar autenticação ao carregar o componente
    useEffect(() => {
        const checkAuth = () => {
            const token = localStorage.getItem('token');
            const user = localStorage.getItem('user');

            if (!token || !user) {
                navigate('/login');
                return;
            }

            try {
                const userData = JSON.parse(user);
                setUser(userData);
                setLoading(false);
            } catch (error) {
                // Token/user inválido, redirecionar para login
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                navigate('/login');
            }
        };

        checkAuth();
    }, [navigate]);

    const [activeTab, setActiveTab] = useState('welcome');

    useEffect(() => {
        if (!user) return;

        loadUserData(user.account);

        // Initialize mobile menu
        initMobileMenu();
    }, [user]); // Mudança: dependência correta é [user]

    // Debug: monitorar mudanças no estado dos characters
    useEffect(() => {
        console.log('Estado characters mudou:', characters.length, 'itens');
    }, [characters]);

    const loadUserData = async (username: string) => {
        try {
            console.log('Iniciando carregamento de dados para:', username);

            const [balanceResponse, charactersResponse] = await Promise.all([
                cashService.getBalance(username),
                charactersService.getMyCharacters()
            ]);

            console.log('Balance response:', balanceResponse);
            console.log('Characters response:', charactersResponse);

            setCashBalance(balanceResponse);
            setCharacters(charactersResponse || []);

            console.log('Personagens definidos no estado:', charactersResponse || []);
        } catch (error) {
            console.error('Erro ao carregar dados do usuário:', error);
            console.error('Detalhes do erro:', (error as any)?.response?.data || (error as Error)?.message);
            setCharacters([]);
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = () => {
        authService.logout();
        navigate('/');
        window.location.reload();
    };

    if (loading) {
        return (
            <div className="usercp-page">
                <div className="container">
                    <div className="loading">{t('usercp.loading')}</div>
                </div>
            </div>
        );
    }

    const currentDate = new Date();
    const formattedDate = currentDate.toLocaleDateString(dateLocale, {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
    });

    return (
        <div className="usercp-page">
            <div className="usercp-wrapper">
                <div className="container">
                    <div className="usercp-layout">
                        {/* Sidebar */}
                        <aside className="usercp-sidebar">
                            <div className="sidebar-user">
                                <div className="sidebar-avatar">
                                    <i className="ri-user-smile-line"></i>
                                </div>
                                <div className="sidebar-user-info">
                                    <h4>{user?.account}</h4>
                                    <span>Knight Icarus Online</span>
                                </div>
                            </div>

                            <div className="mobile-menu-toggle">
                                <span>{t('usercp.menu')}</span>
                                <i className="ri-menu-line"></i>
                            </div>

                            <ul className="sidebar-nav">
                                <li className="nav-label">{t('usercp.accountManagement')}</li>
                                <li className="nav-item">
                                    <button
                                        className={`nav-link ${activeTab === 'welcome' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('welcome')}
                                    >
                                        <i className="ri-dashboard-line"></i>
                                        <span>{t('usercp.overview')}</span>
                                    </button>
                                </li>
                                <li className="nav-item">
                                    <button
                                        className={`nav-link ${activeTab === 'profile' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('profile')}
                                    >
                                        <i className="ri-user-settings-line"></i>
                                        <span>{t('usercp.editProfile')}</span>
                                    </button>
                                </li>
                                <li className="nav-item">
                                    <button
                                        className={`nav-link ${activeTab === 'security' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('security')}
                                    >
                                        <i className="ri-shield-check-line"></i>
                                        <span>{t('usercp.security')}</span>
                                    </button>
                                </li>
                                <li className="nav-item">
                                    <button
                                        className={`nav-link ${activeTab === 'password' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('password')}
                                    >
                                        <i className="ri-lock-password-line"></i>
                                        <span>{t('usercp.changePassword')}</span>
                                    </button>
                                </li>

                                <li className="nav-label">{t('usercp.services')}</li>
                                <li className="nav-item">
                                    <button
                                        className={`nav-link ${activeTab === 'characters' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('characters')}
                                    >
                                        <i className="ri-user-line"></i>
                                        <span>{t('usercp.myCharacters')}</span>
                                    </button>
                                </li>

                                <li className="nav-item">
                                    <button
                                        className={`nav-link ${activeTab === 'promo' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('promo')}
                                    >
                                        <i className="ri-gift-2-line"></i>
                                        <span>{t('usercp.eventPromo')}</span>
                                    </button>
                                </li>

                                <li className="nav-item">
                                    <button
                                        className={`nav-link ${activeTab === 'store' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('store')}
                                    >
                                        <i className="ri-store-3-line"></i>
                                        <span>{t('usercp.store')}</span>
                                    </button>
                                </li>

                                <li className="nav-item">
                                    <Link
                                        to="/donate"
                                        className="nav-link"
                                    >
                                        <i className="ri-bank-card-line"></i>
                                        <span>Doação / Cash</span>
                                    </Link>
                                </li>

                                <li className="nav-label">{t('usercp.supportLabel')}</li>
                                <li className="nav-item">
                                    <button
                                        className={`nav-link ${activeTab === 'support' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('support')}
                                    >
                                        <i className="ri-customer-service-2-line"></i>
                                        <span>{t('usercp.supportRequests')}</span>
                                    </button>
                                </li>
                                <li className="nav-item">
                                    <button
                                        className={`nav-link ${activeTab === 'history' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('history')}
                                    >
                                        <i className="ri-history-line"></i>
                                        <span>{t('usercp.accountHistory')}</span>
                                    </button>
                                </li>

                                <li className="nav-item logout-item">
                                    <button className="nav-link logout-link" onClick={handleLogout}>
                                        <i className="ri-logout-box-line"></i>
                                        <span>{t('usercp.logOut')}</span>
                                    </button>
                                </li>
                            </ul>
                        </aside>

                        {/* Main Content */}
                        <div className="usercp-content-area">
                            {activeTab === 'welcome' && (
                                <>
                                    {/* Welcome Banner */}
                                    <div className="usercp-banner">
                                        <div className="banner-content">
                                            <span className="banner-label">{t('usercp.welcomeBanner')}</span>
                                            <h2>{t('usercp.hello')} {user?.account}</h2>
                                            <p>{t('usercp.welcomeDesc')}</p>
                                        </div>
                                    </div>

                                    {/* Stats Grid */}
                                    <div className="stats-grid">
                                        {/* Balance Stat */}
                                        <div className="stat-card">
                                            <div className="stat-header">
                                                <div className="stat-icon">
                                                    <i className="ri-coins-line"></i>
                                                </div>
                                                <span className="stat-badge success">{t('usercp.active')}</span>
                                            </div>
                                            <span className="stat-label">{t('usercp.yourBalance')}</span>
                                            <strong className="stat-value">{cashBalance.totalBalance}</strong>
                                        </div>

                                        {/* Security Stat */}
                                        <div className="stat-card">
                                            <div className="stat-header">
                                                <div className="stat-icon">
                                                    <i className="ri-shield-check-line"></i>
                                                </div>
                                            </div>
                                            <span className="stat-label">{t('usercp.securityLevel')}</span>
                                            <strong className="stat-value security">{t('usercp.securityLevelValue')}</strong>
                                        </div>

                                        {/* Last Login Stat */}
                                        <div className="stat-card">
                                            <div className="stat-header">
                                                <div className="stat-icon">
                                                    <i className="ri-calendar-check-line"></i>
                                                </div>
                                            </div>
                                            <span className="stat-label">{t('usercp.lastLogin')}</span>
                                            <strong className="stat-value">{formattedDate}</strong>
                                        </div>
                                    </div>

                                    {/* Quick Actions */}
                                    <div className="usercp-section">
                                        <div className="section-header">
                                            <h3>{t('usercp.lastLogin')}</h3>
                                            <Link to="/profile" className="view-all-link">
                                                {t('usercp.viewAll')} <i className="ri-arrow-right-line"></i>
                                            </Link>
                                        </div>

                                        <div className="quick-actions">
                                            <Link to="/store" className="action-card">
                                                <i className="ri-store-3-line"></i>
                                                <span>{t('usercp.store')}</span>
                                                <small>{t('usercp.reviewProducts')}</small>
                                            </Link>

                                            <Link to="/donate" className="action-card">
                                                <i className="ri-bank-card-line"></i>
                                                <span>Doação / Cash</span>
                                                <small>Comprar Cash com Mercado Pago</small>
                                            </Link>

                                            <Link to="/profile" className="action-card">
                                                <i className="ri-user-settings-line"></i>
                                                <span>{t('usercp.profile')}</span>
                                                <small>{t('usercp.editYourInfo')}</small>
                                            </Link>

                                            <Link to="/security" className="action-card">
                                                <i className="ri-shield-keyhole-line"></i>
                                                <span>{t('usercp.security')}</span>
                                                <small>{t('usercp.securitySettings')}</small>
                                            </Link>

                                            <a href="https://discord.gg/ps9smnSu9j" target="_blank" rel="noopener noreferrer" className="action-card">
                                                <i className="ri-discord-line"></i>
                                                <span>Discord</span>
                                                <small>{t('usercp.getSupport')}</small>
                                            </a>
                                        </div>
                                    </div>

                                    {/* Account Summary */}
                                    <div className="usercp-section">
                                        <h3>{t('usercp.accountSummary')}</h3>
                                        <div className="account-summary">
                                            <div className="summary-item">
                                                <span className="summary-label">{t('usercp.registrationDate')}</span>
                                                <span className="summary-value">{formattedDate}</span>
                                            </div>
                                            <div className="summary-item">
                                                <span className="summary-label">{t('usercp.emailStatus')}</span>
                                                <span className="summary-status approved">
                                                    <i className="ri-checkbox-circle-fill"></i> {t('usercp.approved')}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}

                            {activeTab === 'profile' && (
                                <div className="usercp-section">
                                    <h3>Edit Profile</h3>
                                    <p>Update your account information and preferences.</p>
                                    <div className="form-section">
                                        <form className="usercp-form">
                                            <div className="form-group">
                                                <label>Username</label>
                                                <input type="text" value={user?.account} readOnly />
                                            </div>
                                            <div className="form-group">
                                                <label>Email</label>
                                                <input type="email" value={user?.email} readOnly />
                                            </div>
                                            <div className="form-group">
                                                <label>Birth Date</label>
                                                <input type="date" />
                                            </div>
                                            <button type="submit" className="submit-btn">Update Profile</button>
                                        </form>
                                    </div>
                                </div>
                            )}

                            {activeTab === 'security' && (
                                <div className="usercp-section">
                                    <h3>Security Settings</h3>
                                    <p>Configure your account security options.</p>
                                    <div className="security-modules">
                                        <div className="security-card">
                                            <div className="security-icon">
                                                <i className="ri-mail-line"></i>
                                            </div>
                                            <div className="security-content">
                                                <strong>Email Security</strong>
                                                <span>Your email is verified</span>
                                            </div>
                                            <span className="badge-active">ACTIVE</span>
                                        </div>
                                        <div className="security-card">
                                            <div className="security-icon warning">
                                                <i className="ri-smartphone-line"></i>
                                            </div>
                                            <div className="security-content">
                                                <strong>Two-Factor Authentication</strong>
                                                <span>Add an extra layer of security</span>
                                            </div>
                                            <button className="submit-btn small">Enable</button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {activeTab === 'password' && (
                                <div className="usercp-section">
                                    <h3>Change Password</h3>
                                    <p>Update your account password for better security.</p>
                                    <div className="form-section">
                                        <form className="usercp-form" onSubmit={handleChangePassword}>
                                            <div className="form-group">
                                                <label>Current Password</label>
                                                <input
                                                    type="password"
                                                    placeholder="Enter current password"
                                                    value={pwdCurrent}
                                                    onChange={(e) => setPwdCurrent(e.target.value)}
                                                    required
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label>New Password</label>
                                                <input
                                                    type="password"
                                                    placeholder="Enter new password"
                                                    value={pwdNew}
                                                    onChange={(e) => setPwdNew(e.target.value)}
                                                    required
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label>Confirm New Password</label>
                                                <input
                                                    type="password"
                                                    placeholder="Confirm new password"
                                                    value={pwdConfirm}
                                                    onChange={(e) => setPwdConfirm(e.target.value)}
                                                    required
                                                />
                                            </div>
                                            {pwdMsg && (
                                                <div
                                                    style={{
                                                        padding: '8px 12px',
                                                        marginBottom: '10px',
                                                        borderRadius: '6px',
                                                        background: pwdMsg.ok ? '#d4edda' : '#f8d7da',
                                                        color: pwdMsg.ok ? '#155724' : '#721c24',
                                                        fontSize: '14px',
                                                    }}
                                                >
                                                    {pwdMsg.text}
                                                </div>
                                            )}
                                            <button type="submit" className="submit-btn" disabled={pwdSaving}>
                                                {pwdSaving ? 'Aguarde...' : 'Change Password'}
                                            </button>
                                        </form>
                                    </div>
                                </div>
                            )}

                            {activeTab === 'store' && (
                                <div className="usercp-section">
                                    <h3>Loja</h3>
                                    <p>Comprar itens na loja de cash e adicionar Cash via doação.</p>
                                    <div className="store-info">
                                        <div className="current-balance">
                                            <h4>Saldo atual</h4>
                                            <p className="balance-amount">{cashBalance.totalBalance} Cash</p>
                                        </div>
                                        <div className="store-actions">
                                            <Link to="/store" className="submit-btn">
                                                Ir para loja de itens
                                            </Link>
                                            <Link to="/donate" className="submit-btn secondary">
                                                Loja de doação (Cash)
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {activeTab === 'characters' && (
                                <div className="usercp-section">
                                    <h3>{t('usercp.myCharactersTitle')}</h3>
                                    <p>{t('usercp.myCharactersDesc')}</p>
                                    <div className="characters-list">
                                        {characters.length > 0 ? (
                                            characters.map((character: any) => (
                                                <div key={character.dbKey} className="character-card">
                                                    <div className="character-header">
                                                        <div className="character-avatar">
                                                            <i className="ri-user-line"></i>
                                                        </div>
                                                        <div className="character-info">
                                                            <h4>{character.name}</h4>
                                                            <span className="character-class">{character.className}</span>
                                                            <span className="character-level">{t('usercp.level')} {character.level}</span>
                                                        </div>
                                                        <div className="character-status">
                                                            {character.isOnline ? (
                                                                <span className="status-online">
                                                                    <i className="ri-radio-button-line"></i> {t('usercp.online')}
                                                                </span>
                                                            ) : (
                                                                <span className="status-offline">
                                                                    <i className="ri-radio-button-line"></i> {t('usercp.offline')}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="character-details">
                                                        <div className="detail-item">
                                                            <span>{t('usercp.race')}:</span>
                                                            <span>{character.raceName}</span>
                                                        </div>
                                                        <div className="detail-item">
                                                            <span>{t('usercp.gender')}:</span>
                                                            <span>{character.female ? (locale === 'pt-BR' ? 'Feminino' : 'Female') : (locale === 'pt-BR' ? 'Masculino' : 'Male')}</span>
                                                        </div>
                                                        <div className="detail-item">
                                                            <span>{t('usercp.location')}:</span>
                                                            <span>{character.mapId || (locale === 'pt-BR' ? 'Desconhecido' : 'Unknown')}</span>
                                                        </div>
                                                        <div className="detail-item">
                                                            <span>{t('usercp.money')}:</span>
                                                            <span>{character.money?.toLocaleString(dateLocale) || 0} {t('usercp.gold')}</span>
                                                        </div>
                                                        <div className="detail-item">
                                                            <span>{t('usercp.lastLoginChar')}:</span>
                                                            <span>{new Date(character.lastAccessDate).toLocaleDateString(dateLocale, { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="no-characters">
                                                <i className="ri-user-line"></i>
                                                <p>{t('usercp.noCharacters')}</p>
                                                <small>{t('usercp.noCharactersHint')}</small>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {activeTab === 'promo' && (
                                <div className="usercp-section">
                                    <h3>{t('usercp.eventPromo')}</h3>
                                    <p>{t('usercp.eventPromoDesc')}</p>
                                    <PromoCodeSection />
                                </div>
                            )}

                            {activeTab === 'support' && (
                                <div className="usercp-section">
                                    <h3>Support</h3>
                                    <p>Get help with your account and game issues.</p>
                                    <div className="support-actions">
                                        <a href="https://discord.com/channels/1463913142328234223/1464036874992488603" target="_blank" rel="noopener noreferrer" className="submit-btn">
                                            <i className="ri-external-link-line"></i> Create New Ticket (Discord)
                                        </a>
                                        <a href="https://discord.gg/ps9smnSu9j" target="_blank" rel="noopener noreferrer" className="submit-btn secondary">
                                            <i className="ri-discord-line"></i> Join Discord Server
                                        </a>
                                    </div>
                                    <div className="support-info">
                                        <p><strong>Need help?</strong></p>
                                        <p>Join our Discord server and create a support ticket in the dedicated channel. Our support team will assist you as soon as possible.</p>
                                    </div>
                                </div>
                            )}

                            {activeTab !== 'welcome' && (
                                <div className="back-to-dashboard">
                                    <button className="submit-btn secondary" onClick={() => setActiveTab('welcome')}>
                                        <i className="ri-arrow-left-line"></i> Back to Dashboard
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserCP;